import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Ce qu'un menu déroulant doit fermer, et qu'on oublie à moitié en l'écrivant à la main.
 *
 * Trois fermetures, et la troisième manque presque toujours : le clic hors du menu, `Échap` **en
 * rendant le focus au déclencheur**, et la sortie du focus.
 *
 * Le dernier point est celui qui manque presque toujours. Sans lui, on tabule hors du menu et il
 * reste ouvert derrière, à recouvrir le contenu qu'on est en train de lire.
 */
export function useDropdown() {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const close = useCallback((giveFocusBack = false) => {
        setOpen(false);
        if (giveFocusBack) triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return;

        // `pointerdown` et non `click` : un clic dont le bouton s'enfonce hors du menu doit le
        // refermer même si le relâchement se fait ailleurs, et cela évite la course avec le
        // `onClick` du déclencheur, qui s'exécuterait après la fermeture et rouvrirait.
        const onPointerDown = (event: PointerEvent) => {
            if (!(event.target instanceof Node)) return;
            if (rootRef.current?.contains(event.target) === true) return;
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close(true);
        };
        const onFocusIn = (event: FocusEvent) => {
            if (!(event.target instanceof Node)) return;
            if (rootRef.current?.contains(event.target) === true) return;
            setOpen(false);
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('focusin', onFocusIn);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('focusin', onFocusIn);
        };
    }, [open, close]);

    return {open, setOpen, close, rootRef, triggerRef};
}
